// ── Ce que le calculateur propose au visiteur ───────────────────────────────
// GPU les moins chers, formats de poids, grille « Ça tient ? », autres modèles qui tiennent, et la
// cohérence entre GPU, moteur, poids et cache KV quand l'un d'eux change. Fonctions pures, sans React :
// vérifiées par scripts/check-vram.ts et scripts/check-invariants.ts comme le calcul. Les bugs
// « FP8 proposé sur un A100 » et « GGUF proposé avec vLLM » (octobre 2026) vivaient dans le composant.

import type { Inputs } from "./core.ts";
import type { Target } from "./reserve.ts";
import {
  GPUS, WEIGHT_FORMATS, adaptFormat, defaultEngine, engineFor, fitFormatsFor, formatOk, formatRuns, ggufOnly, isConsole, kvFallback, kvRuns,
  offersFor, runsEngine, usableMemGiB, type Gpu, type GpuPrices,
} from "./hardware.ts";
import { compute, engineBits, minGpus, splitFor, weightBytes } from "./memory.ts";
import { MODELS, type ModelPreset } from "./models.ts";
import { offloadPlan } from "./performance.ts";

/** Taille exacte des fichiers d'un format quand on la connaît (versions GGUF publiées), sinon undefined. */
export type ExactSize = (model: ModelPreset, formatId: string) => number | undefined;
type Split = { tp: number; pp: number };

/** GPU qui tiennent cette charge dans un seul serveur (8 cartes au plus), du moins cher au plus cher à
 *  louer ; seulement ceux où ce moteur tourne et qui font tourner ce format de poids. */
export function cheapestSetups(base: Inputs, engine: string, formatId: string, prices: GpuPrices, training = false, limit = 8) {
  return GPUS
    .map((g) => {
      // Ce GPU, avec ce que le moteur y réserve (reserve.ts).
      const n = minGpus({ ...base, vramGiB: usableMemGiB(g), target: base.target && { ...base.target, gpu: g } });
      const best = offersFor(prices, g.id)[0];
      return { g, n: n ?? 0, cost: n && best ? n * best.price : Infinity, provider: best?.name.split(" (")[0] };
    })
    // Entraînement : jamais sur console (PS5LM Plus ne fait que de l'inférence).
    .filter((o) => o.n > 0 && o.n <= 8 && (training ? !isConsole(o.g) : runsEngine(o.g, engine) && formatOk(o.g, engine, base.model, formatId)))
    .sort((a, b) => a.cost - b.cost || a.n - b.n || a.g.vram - b.g.vram)
    .slice(0, limit);
}

/** Formats de poids proposés pour ce GPU et ce moteur (le checkpoint officiel compris, s'il y tourne),
 *  du plus lourd au plus léger, avec le nombre de GPU qu'il faut pour chacun. */
export function formatChoices(base: Inputs, gpu: Gpu | undefined, engine: string, exact: ExactSize) {
  const model = base.model;
  return WEIGHT_FORMATS
    .filter((f) => f.id !== "fp32" && (f.id !== "native" || !!model.native) && formatOk(gpu, engine, model, f.id))
    .map((f) => ({
      f, size: exact(model, f.id) ?? weightBytes(model, engineBits({ ...base, weightBits: f.bits })),
      n: minGpus({ ...base, weightBits: f.bits, exactWeightBytes: exact(model, f.id) }),
    }))
    .sort((a, b) => b.size - a.size);
}

/** Déchargement vers la RAM de llama.cpp sur une carte grand public : réglages de llama.cpp et RAM du
 *  visiteur, comme le volet « Use your system RAM » du calculateur. */
export type RamOffload = { gpu: Gpu; ram: { gib: number; bandwidth: number }; utilization: number; overheadGiB: number; bwEfficiency: number };
export type FitStatus = "ok" | "tight" | "oom" | "ram";
/** llama.cpp sur la carte du déchargement : sa marge « --fit » et son tampon de calcul (reserve.ts). */
const llamaTarget = (offload: RamOffload, base: Inputs): Target => ({ engine: "llamacpp", gpu: offload.gpu, display: base.target?.display });

/** Grille « Ça tient ? » : les formats que le site propose pour ce moteur, à chaque longueur de contexte
 *  jusqu'à celle du modèle, sur le matériel choisi (même nombre de GPU, mêmes requêtes). Avec `offload`,
 *  une case qui ne tient pas en VRAM mais tient avec la RAM devient « ram » : sinon une 5090 et
 *  Qwen3.8-Flash-Next donnaient une grille de croix sous un « Fits with RAM ». */
export function fitGrid(base: Inputs, split: Split, gpu: Gpu | undefined, engine: string, choices: ReturnType<typeof formatChoices>, exact: ExactSize, offload?: RamOffload) {
  const model = base.model;
  const contexts = [...new Set([4096, 8192, 32768, 131072, 262144, 1048576].filter((c) => c < model.maxContext)
    .concat(Number.isFinite(model.maxContext) ? [model.maxContext] : []))];
  const fit = fitFormatsFor(gpu, engine);
  const gpus = split.tp * split.pp;
  const rows = choices.filter(({ f }) => fit.includes(f.id)).map(({ f }) => ({
    f,
    cells: contexts.map((context): FitStatus => {
      const cell = { ...base, weightBits: f.bits, exactWeightBytes: exact(model, f.id), context };
      const status = compute({ ...cell, ...split }).status;
      if (status !== "oom" || !offload) return status;
      const withRam = offloadPlan({ ...cell, ...splitFor(gpus, true), layerSplit: true, utilization: offload.utilization, overheadGiB: offload.overheadGiB, target: llamaTarget(offload, base) },
        offload.gpu, gpus, offload.ram, offload.bwEfficiency);
      // Pas de plan ni de RAM manquante : avec les réglages de llama.cpp, tout tient déjà en VRAM (à la limite).
      return withRam.plan ? "ram" : withRam.ramShort === undefined ? "tight" : "oom";
    }),
  }));
  return { contexts, rows };
}

/** Pastille du sélecteur de modèles. ok : tient sur `gpus` GPU ; ram : avec la RAM (carte grand public,
 *  llama.cpp) ; multi : sur `gpus` GPU de plus (8 au plus) ; no : même pas. */
export type ModelFit = { status: "ok" | "ram" | "multi" | "no"; gpus?: number; formatId: string };

/** Ce modèle (base.model) tourne-t-il sur ce matériel, au format 4 bits que ce moteur charge (Q4_K_M
 *  avec llama.cpp, INT4 GPTQ / AWQ avec vLLM ; sinon le plus léger qu'il propose), avec le contexte et
 *  les requêtes du visiteur ? La question que la liste des modèles ne posait pas (octobre 2026). */
export function modelFit(base: Inputs, gpu: Gpu | undefined, engine: string, gpus: number, exact: ExactSize, offload?: RamOffload): ModelFit {
  const model = base.model;
  const formatId = [engine === "llamacpp" ? "q4_k_m" : "int4", ...[...fitFormatsFor(gpu, engine)].reverse()]
    .find((id) => (id !== "native" || !!model.native) && formatOk(gpu, engine, model, id)) ?? "bf16";
  const layerSplit = engine === "llamacpp";
  const i: Inputs = { ...base, weightBits: WEIGHT_FORMATS.find((f) => f.id === formatId)?.bits ?? 16, exactWeightBytes: exact(model, formatId), layerSplit };
  if (compute({ ...i, ...splitFor(gpus, layerSplit) }).status !== "oom") return { status: "ok", formatId };
  if (offload) {
    const r = offloadPlan({ ...i, ...splitFor(gpus, true), layerSplit: true, utilization: offload.utilization, overheadGiB: offload.overheadGiB, target: llamaTarget(offload, base) },
      offload.gpu, gpus, offload.ram, offload.bwEfficiency);
    if (r.plan) return { status: "ram", formatId };
    // Ni plan ni RAM manquante : avec les réglages de llama.cpp, tout tient déjà en VRAM.
    if (r.ramShort === undefined) return { status: "ok", formatId };
  }
  const n = minGpus(i);
  return n !== null && n <= 8 ? { status: "multi", gpus: n, formatId } : { status: "no", formatId };
}

/** Les autres modèles de la liste qui tiennent sur ce matériel, chacun au format le plus fidèle qui passe
 *  et qui tourne ici avec ce moteur, du plus grand au plus petit. */
export function otherModelsThatFit(base: Inputs, split: Split, gpu: Gpu, engine: string, exact: ExactSize, limit = 8, models = MODELS) {
  const fit = fitFormatsFor(gpu, engine);
  return models.filter((m) => m.id !== base.model.id && !m.preview).flatMap((m) => {
    const f = WEIGHT_FORMATS.filter((x) => fit.includes(x.id) && (x.id !== "native" || m.native) && formatOk(gpu, engine, m, x.id))
      .find((x) => compute({ ...base, model: m, ...split, weightBits: x.bits, exactWeightBytes: exact(m, x.id) }).status !== "oom");
    return f ? [{ m, f }] : [];
  }).sort((a, b) => b.m.params - a.m.params).slice(0, limit);
}

// ── Cohérence GPU, moteur, poids, cache ─────────────────────────────────────
// Chaque changement du visiteur produit une configuration qui tourne : un moteur présent sur ce GPU,
// des poids que ce moteur charge, un cache qu'il sait allouer. Un choix explicite reste possible même
// sans support matériel (FP8 sur un A100) : l'avertissement de formatWarnings le dit alors.

export type Setup = { gpu?: Gpu; engine: string; weights: string; kv: string };

/** GPU changé : le moteur de ce type de carte (vLLM en datacenter, llama.cpp ailleurs) sauf choix du
 *  visiteur, toujours un moteur qui y tourne ; les poids équivalents (FP8 → INT8 sur un A100, Q8_0
 *  avec llama.cpp) ; pas de cache FP8 sans FP8. */
export function setupAfterGpu(model: ModelPreset, s: Setup, gpu: Gpu, engineChosen: boolean): Setup {
  let engine = engineChosen ? s.engine : defaultEngine(gpu);
  if (!runsEngine(gpu, engine)) engine = ggufOnly(gpu) ? "llamacpp" : "vllm";
  let kv = !gpu.fp8 && s.kv === "fp8" ? "bf16" : s.kv;
  if (!kvRuns(engine, kv)) kv = kvFallback(engine, kv, gpu);
  return { gpu, engine, weights: adaptFormat(engine, gpu, model, s.weights), kv };
}

/** Moteur changé : les poids et le cache équivalents qu'il sait charger (Q4_K_M → INT4 avec vLLM). */
export function setupAfterEngine(model: ModelPreset, s: Setup, engine: string): Setup {
  return { ...s, engine, weights: adaptFormat(engine, s.gpu, model, s.weights), kv: kvRuns(engine, s.kv) ? s.kv : kvFallback(engine, s.kv, s.gpu) };
}

/** Poids choisis : le moteur qui les charge (GGUF → llama.cpp) ; si aucun ne tourne sur ce GPU (FP8 sur
 *  Mac), leur équivalent pour le moteur actuel. */
export function setupAfterWeights(model: ModelPreset, s: Setup, weights: string): Setup {
  const engine = engineFor(weights, model, s.gpu, s.engine);
  if (!engine) return { ...s, weights: adaptFormat(s.engine, s.gpu, model, weights) };
  return { ...s, engine, weights, kv: kvRuns(engine, s.kv) ? s.kv : kvFallback(engine, s.kv, s.gpu) };
}

/** Modèle choisi : son checkpoint officiel s'il tourne ici (sinon l'équivalent), et un cache FP8
 *  calibré seulement si le GPU a du FP8 et que le moteur sait l'allouer. */
export function setupAfterModel(model: ModelPreset, s: Setup): Setup {
  const weights = model.native ? adaptFormat(s.engine, s.gpu, model, "native") : s.weights === "native" ? "bf16" : s.weights;
  const kv = model.fp8Kv && (!s.gpu || s.gpu.fp8) && kvRuns(s.engine, "fp8") ? "fp8" : s.kv;
  return { ...s, weights, kv };
}

/** Configuration venue d'ailleurs (lien partagé, ancien réglage) rendue possible, sans rien changer de ce
 *  qui tourne déjà : moteur présent sur ce GPU, cache qu'il alloue, poids qu'il charge. */
export function coherentSetup(model: ModelPreset, s: Setup): Setup {
  const engine = s.gpu && !runsEngine(s.gpu, s.engine) ? (ggufOnly(s.gpu) ? "llamacpp" : "vllm") : s.engine;
  const kv = kvRuns(engine, s.kv) ? s.kv : kvFallback(engine, s.kv, s.gpu);
  const weights = engine !== s.engine || !formatRuns(engine, model, s.weights) ? adaptFormat(engine, s.gpu, model, s.weights) : s.weights;
  return { ...s, engine, kv, weights };
}
